import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { signupService } from '@/features/user/services/signupService';
import { cartService } from '@/features/cart/services/cartService';
import { likeService } from '@/features/likes/services/likeService';
import type { SignupRequest, SignupFormData } from '@/features/user/types/types';

export const useSignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setError, login, setPendingSignupData, pendingSignupData, isEmailVerified, setIsEmailVerified, refetch: refetchAuth } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sendVerificationCode = async (formData: SignupFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Store complete form data for verification
      setPendingSignupData(formData);

      // Simulate sending verification code
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Navigate to confirmation page, preserving returnUrl if present
      const returnUrl = searchParams.get('returnUrl');
      navigate(returnUrl ? `/confirmation-code?returnUrl=${encodeURIComponent(returnUrl)}` : '/confirmation-code');

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send verification code';
      setError(errorMessage);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const verifyEmail = async (code: string) => {
    setError(null);

    try {
      const isValid = await signupService.verifyConfirmationCode(code);

      if (isValid) {
        setIsEmailVerified(true);
        // Return to signup page, preserving returnUrl
        const returnUrl = searchParams.get('returnUrl');
        navigate(returnUrl ? `/signup?returnUrl=${encodeURIComponent(returnUrl)}` : '/signup');
        return true;
      } else {
        setError('Invalid confirmation code');
        return false;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to verify code';
      setError(errorMessage);
      return false;
    }
  };

  const completeSignup = async (formData: SignupFormData) => {
    if (!isEmailVerified) {
      throw new Error('Email must be verified before creating account');
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Convert form data to API format
      const signupRequest: SignupRequest = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        password: formData.password
      };

      // Call signup API
      const response = await signupService.signup(signupRequest);

      // Store user data in auth store
      login(response.data.user);

      // Claim guest likes FIRST (before cart claim removes guest cookies)
      await likeService.claimLikes().catch((error) => {
        // Don't fail signup if claim fails - log and continue
        console.error('Failed to claim guest likes after signup:', error);
      });

      // Claim guest cart (moves cart ownerships from guest to real user)
      // Note: This removes guest cookies, so must be called after claimLikes
      await cartService.claimGuestCart().catch((error) => {
        // Don't fail signup if claim fails - log and continue
        console.error('Failed to claim guest cart after signup:', error);
      });

      // Cart count will auto-refresh via header's stale-while-revalidate

      // Refetch AuthProvider to sync with new authentication state
      await refetchAuth();

      // Reset verification state
      setIsEmailVerified(false);
      setPendingSignupData(null);

      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create account';
      setError(errorMessage);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearFormData = () => {
    setPendingSignupData(null);
    setIsEmailVerified(false);
  };

  return {
    sendVerificationCode,
    verifyEmail,
    completeSignup,
    clearFormData,
    isSubmitting,
    isEmailVerified,
    pendingSignupData,
  };
};
